import { AddImageIcon, CircleCloseIcon } from 'assets/icons';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import CustomInput from 'components/custom-input.tsx/CustomInput';
import CustomModal from 'components/custom-modal/CustomModal';
import FileUpload from 'components/file-upload/FileUpload';
import { FILE_TYPE } from 'constants/common';
import { FC, useEffect, useState } from 'react';
import { VoidFn } from 'types/baseTypes';

interface AddAttachmentModalProps {
  className?: string;
  isEdit: boolean;
  isUploadingImage: boolean;
  onClose: VoidFn;
  onSave: (image) => void;
  attachmentData: any;
}

const AddAttachmentModal: FC<AddAttachmentModalProps> = ({
  isEdit,
  isUploadingImage,
  onClose,
  onSave,
  attachmentData
}) => {
  const [imagePreview, setImagePreview] = useState();
  const [attachmentTitle, setAttachmentTitle] = useState('');
  const [imageFile, setImageFile] = useState();
  const [hasImageChanged, setHasImageChanged] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setImagePreview(attachmentData?.imagePreview);
      setAttachmentTitle(attachmentData?.title);
    }
  }, [attachmentData]);

  const handleTitleChange = (value: string) => {
    setAttachmentTitle(value);
  };

  const fileLoadEndCallback = ({ file }: { file: File }) => {
    handleTitleChange(file?.name);
  };

  const removeImagePreview = () => {
    setImagePreview(null);
    setImageFile(null);
    setAttachmentTitle('');
    setHasImageChanged(true);
  };

  const handleSave = () => {
    onSave({ image: imageFile, title: attachmentTitle, imagePreview, hasImageChanged });
  };

  const handleClose = () => {
    setImagePreview(null);
    setImageFile(null);
    setAttachmentTitle('');
    onClose();
  };

  return (
    <CustomModal open={true} width="md:w-[610px]">
      <div className="relative px-10 py-9">
        <div className="mb-3.5 text-xl font-bold text-heading">Add photo</div>
        <CircleCloseIcon className="absolute top-[9px] right-[9px] cursor-pointer" onClick={handleClose} />
        <FileUpload
          id="create-invoice-add-attachment-card"
          isUploadInProgress={isUploadingImage}
          prefill={isEdit}
          setFile={setImageFile}
          removePreview={removeImagePreview}
          filePreview={imagePreview}
          setPreview={setImagePreview}
          className="mb-5"
          FileUploadIcon={AddImageIcon}
          supportedFileTypes={{
            [FILE_TYPE.JPG]: [],
            [FILE_TYPE.JPEG]: [],
            [FILE_TYPE.PNG]: [],
            [FILE_TYPE.WEBP]: []
          }}
          allowImageCompression={true}
          onFileLoadEnd={fileLoadEndCallback}
          allowMultiple={false}
        />
        <CustomInput
          id="add-photo-title"
          name="title"
          onChange={handleTitleChange}
          value={attachmentTitle}
          autoFocus={true}
          placeholder="Add title"
          className="mb-4"
        />
        <div className="flex items-center justify-end gap-5">
          <CustomButton
            className="h-[38px] w-20"
            type={ButtonType.SECONDARY}
            id="attachment-modal-cancel"
            onClick={handleClose}>
            Cancel
          </CustomButton>
          <CustomButton
            className="h-[38px] w-20"
            type={ButtonType.PRIMARY}
            id="attachment-modal-save"
            onClick={handleSave}
            disabled={!attachmentTitle || !imagePreview}>
            Save
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default AddAttachmentModal;
